import { StockItem } from "../../types/stocks";
import {
  BUDGET_ETF_FILTERS,
  IBudgetFriendlyETF,
  IBudgetFriendlyETFsResponse,
  LTP_FILTER_QUERY_PARAM,
} from "./BudgetETFTypes";

export const mapEtfToStockItem = (etf: IBudgetFriendlyETF): StockItem => ({
  changePrice: String(etf?.changePrice),
  exchange: etf?.exchange,
  fullName: etf?.fullName,
  logoURL: "",
  ltp: etf?.ltp,
  perChange: String(etf?.perChange),
  redirectURL: etf?.redirectURL ?? etf?.formattedSymbol,
  stockStatus: etf?.stockStatus,
  symbol: etf?.symbol,
  token: etf?.token,
});

export const mapResponseToStocks = (
  response?: IBudgetFriendlyETFsResponse | null,
): StockItem[] => {
  const etfList = Array.isArray(response?.etfList) ? response.etfList : [];

  return etfList
    .filter((etf): etf is IBudgetFriendlyETF => !!etf?.symbol)
    .map(mapEtfToStockItem);
};

export const isValidLtpFilter = (value?: string | null): value is string =>
  !!value && BUDGET_ETF_FILTERS.some((filter) => filter.value === value);

export const resolveLtpFilter = (value?: string | null): string =>
  isValidLtpFilter(value)
    ? value
    : (BUDGET_ETF_FILTERS[0]?.value ?? "<100");

export type BudgetEtfSortMode =
  | "default"
  | "amount_high_to_low"
  | "amount_low_to_high";

export const getNextSortMode = (
  current: BudgetEtfSortMode,
): BudgetEtfSortMode => {
  if (current === "default") return "amount_high_to_low";
  if (current === "amount_high_to_low") return "amount_low_to_high";
  return "amount_high_to_low";
};

export const resolveSortForApi = (
  sortMode: BudgetEtfSortMode,
  defaultSort?: string,
): string => {
  if (sortMode === "amount_high_to_low" || sortMode === "amount_low_to_high") {
    return sortMode;
  }

  return defaultSort ?? "";
};

export const appendLtpFilterToHref = (
  href: string,
  ltpFilter: string,
): string => {
  const separator = href.includes("?") ? "&" : "?";
  return `${href}${separator}${LTP_FILTER_QUERY_PARAM}=${encodeURIComponent(ltpFilter)}`;
};

export const BUDGET_ETF_LAYOUT_LIST_KEY = "budgetFriendlyEtfsLayoutList";
export const BUDGET_ETF_MAIN_NODE_KEY = "budgetFriendlyEtfsMain";

/** Maps AEM tableNodeKey to the layout-list main component key. */
export const resolveBudgetEtfMainNodeKey = (
  tableNodeKey?: string | null,
): string => {
  if (!tableNodeKey) return BUDGET_ETF_MAIN_NODE_KEY;

  const normalized = tableNodeKey.toLowerCase().replace(/[^a-z0-9]/g, "");
  if (normalized === "budgetfriendlyetfs") {
    return BUDGET_ETF_MAIN_NODE_KEY;
  }

  return tableNodeKey;
};

export const formatBudgetEtfLtp = (ltp?: number): string => {
  if (ltp == null || Number.isNaN(Number(ltp))) return "₹0.00";
  return `₹${Number(ltp).toFixed(2)}`;
};

export const formatBudgetEtfChange = (
  changePrice?: string | number,
  perChange?: string | number,
): string => {
  const isPositive = Number(perChange) >= 0;
  const pricePrefix = isPositive ? "+" : "";
  const change = changePrice ?? "0";
  const percent = perChange ?? "0";
  return `${change} (${pricePrefix}${Number(percent).toFixed(2)}%)`;
};
